import { useEffect, useState } from "react";

import { DialogBody, DialogHeader } from "common/components/ui/Dialog";
import { DrawerBody, DrawerHeader } from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import {
  typedObjectKeys,
  typeSafeObjectFromEntries,
} from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { LEAD_LIST_SOURCE_TYPES } from "../../../constants";
import LinkedInEvent from "./LinkedInEvent";
import LinkedInGroup from "./LinkedInGroup";
import LinkedInPost from "./LinkedInPost";
import SourceRow from "./SourceRow";

import type {
  EventLeadListSource,
  GroupLeadListSource,
  PostLeadListSource,
} from "../../../types";

interface LinkedInImportProps {
  createSource: (params: {
    miner: string;
    settings:
      | EventLeadListSource["settings"]
      | GroupLeadListSource["settings"]
      | PostLeadListSource["settings"];
  }) => void;
  isCreatingSource: boolean;
  goBack: () => void;
}

const LINKEDIN_SEARCH_OPTIONS_KEYS = ["search", "navigator"] as const;
const LINKEDIN_ENGAGEMENT_OPTIONS_KEYS = ["post", "event", "group"] as const;

export const LINKEDIN_SEARCH_OPTIONS = typeSafeObjectFromEntries(
  LINKEDIN_SEARCH_OPTIONS_KEYS.map((key) => {
    const { icon, label, subtitle, iconContainerClassName } =
      LEAD_LIST_SOURCE_TYPES[key];
    return [key, { icon, label, subtitle, iconContainerClassName }] as const;
  }),
);

export const LINKEDIN_ENGAGEMENT_OPTIONS = typeSafeObjectFromEntries(
  LINKEDIN_ENGAGEMENT_OPTIONS_KEYS.map((key) => {
    const { icon, label, subtitle, iconContainerClassName } =
      LEAD_LIST_SOURCE_TYPES[key];
    return [key, { icon, label, subtitle, iconContainerClassName }] as const;
  }),
);
export default function LinkedInImport({
  createSource,
  isCreatingSource,
  goBack,
}: LinkedInImportProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id } = useSelectedWorkspaceContext();
  const { workspaceAccounts } = useWorkspaceAccounts(id);

  const [selectedSource, setSelectedSource] =
    useState<keyof typeof LINKEDIN_ENGAGEMENT_OPTIONS>();
  const [accountId, setAccountId] = useState<string>();

  useEffect(() => {
    if (workspaceAccounts?.length) {
      setAccountId(workspaceAccounts[0].id);
    }
  }, [workspaceAccounts]);

  if (selectedSource) {
    const commonProps = {
      accountId,
      setAccountId,
      isCreatingSource,
      createSource: (
        settings:
          | PostLeadListSource["settings"]
          | EventLeadListSource["settings"]
          | GroupLeadListSource["settings"],
      ) => createSource({ settings, miner: accountId }),
      goBack: () => setSelectedSource(undefined),
    };
    if (selectedSource === "post") {
      return <LinkedInPost {...commonProps} />;
    }
    if (selectedSource === "event") {
      return <LinkedInEvent {...commonProps} />;
    }
    if (selectedSource === "group") {
      return <LinkedInGroup {...commonProps} />;
    }
  }

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;

  return (
    <>
      <Header title="Import from LinkedIn" onBack={goBack} />
      <Body>
        <h6 className="pl-1 mb-1.5 text-black-400 text-button-xs">Search</h6>
        <div className="flex flex-col">
          {typedObjectKeys(LINKEDIN_SEARCH_OPTIONS).map((key) => (
            <SourceRow key={key} sourceType={key} to="" />
          ))}
        </div>

        <h6 className="pl-1 mb-1.5 text-black-400 text-button-xs mt-6">
          Engagement
        </h6>
        <div className="flex flex-col">
          {typedObjectKeys(LINKEDIN_ENGAGEMENT_OPTIONS).map((key) => (
            <SourceRow
              key={key}
              sourceType={key}
              onClick={() => setSelectedSource(key)}
            />
          ))}
        </div>
      </Body>
    </>
  );
}
